/**
 * Detail / diff view for comparing two invoice version snapshots.
 */

import { Modal, Button } from '@elsapiens/ui'
import { ArrowLeft, FileText } from 'lucide-react'

import type { InvoiceVersion } from '@/types/billing'

import { formatShortDate, formatVersionDate } from './invoice-version-types'
import { SnapshotPanel } from './SnapshotPanel'

import type { RawVersion, RawInvoiceSnapshot } from './invoice-version-types'

interface VersionDetailViewProps {
  selectedVersion: RawVersion
  compareVersion: RawVersion | null
  versions: InvoiceVersion[]
  isCompareLoading: boolean
  baseCurrency: string
  onChangeCompare: (versionNumber: number | null) => void
  onBack: () => void
  onClose: () => void
}

function CompareVersionButton({ v, compareVersion, isCompareLoading, onChangeCompare }: {
  v: InvoiceVersion; compareVersion: RawVersion | null; isCompareLoading: boolean; onChangeCompare: (v: number | null) => void
}): JSX.Element {
  const rawSnap = v.invoiceSnapshot as unknown as RawInvoiceSnapshot
  const isActive = compareVersion?.versionNumber === v.versionNumber
  const activeClass = 'bg-accent border-primary text-primary font-semibold'
  const inactiveClass = 'border-border text-foreground-secondary hover:bg-surface hover:text-foreground'
  return (
    <button
      key={v.id}
      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${isActive ? activeClass : inactiveClass}`}
      disabled={isCompareLoading}
      type="button"
      onClick={() => { onChangeCompare(v.versionNumber) }}
    >
      v{v.versionNumber}
      <span className="opacity-60">{formatShortDate(v.createdAt)}</span>
      {rawSnap.invoice_number && <span className="opacity-60 font-mono">{rawSnap.invoice_number}</span>}
    </button>
  )
}

function CompareLegend(): JSX.Element {
  return (
    <div className="ml-auto flex flex-wrap gap-3 text-xs text-foreground-secondary">
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border-l-2 border-amber-400 bg-amber-50 shrink-0" />Changed</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-100 dark:bg-green-900 shrink-0" />Added</span>
      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-100 dark:bg-red-900 shrink-0" />Removed</span>
    </div>
  )
}

function CompareBar({ versions, selectedVersion, compareVersion, isCompareLoading, onChangeCompare }: {
  versions: InvoiceVersion[]; selectedVersion: RawVersion; compareVersion: RawVersion | null
  isCompareLoading: boolean; onChangeCompare: (v: number | null) => void
}): JSX.Element {
  const otherVersions = versions.filter(v => v.versionNumber !== selectedVersion.versionNumber)
  const noneClass = !compareVersion ? 'bg-surface-secondary border-border text-foreground-secondary font-semibold' : 'border-border text-foreground-secondary hover:bg-surface hover:text-foreground'
  return (
    <div className="flex flex-wrap items-center gap-3 border border-border rounded-lg el-p-sm bg-surface-secondary">
      <span className="text-xs font-medium text-foreground-secondary shrink-0">Compare against:</span>
      <div className="flex flex-wrap gap-1.5">
        {otherVersions.map(v => <CompareVersionButton key={v.id} compareVersion={compareVersion} isCompareLoading={isCompareLoading} v={v} onChangeCompare={onChangeCompare} />)}
        <button className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${noneClass}`} disabled={isCompareLoading} type="button" onClick={() => { onChangeCompare(null) }}>None</button>
      </div>
      {!!compareVersion && <CompareLegend />}
    </div>
  )
}

function SideBySidePanels({ selectedVersion, compareVersion, baseCurrency }: {
  selectedVersion: RawVersion; compareVersion: RawVersion; baseCurrency: string
}): JSX.Element {
  const snap = selectedVersion.invoiceSnapshot
  const items = selectedVersion.itemsSnapshot ?? []
  const compareSnap = compareVersion.invoiceSnapshot
  const compareItems = compareVersion.itemsSnapshot ?? []
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-foreground-secondary bg-surface-secondary rounded px-2 py-1">Version {compareVersion.versionNumber}</span>
          <span className="text-xs text-foreground-secondary">{formatShortDate(compareVersion.createdAt)}</span>
          <span className="text-xs text-foreground-secondary">· {compareVersion.createdBy}</span>
        </div>
        <SnapshotPanel currency={baseCurrency} items={compareItems} prevItems={items} snap={compareSnap ?? snap} />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-semibold bg-accent text-primary rounded px-2 py-1">Version {selectedVersion.versionNumber}</span>
          <span className="text-xs text-foreground-secondary">{formatShortDate(selectedVersion.createdAt)}</span>
          <span className="text-xs text-foreground-secondary">· {selectedVersion.createdBy}</span>
        </div>
        <SnapshotPanel currency={baseCurrency} items={items} prevItems={compareItems} prevSnap={compareSnap} snap={snap} />
      </div>
    </div>
  )
}

export function VersionDetailView({
  selectedVersion, compareVersion, versions, isCompareLoading,
  baseCurrency, onChangeCompare, onBack, onClose,
}: VersionDetailViewProps): JSX.Element {
  const snap = selectedVersion.invoiceSnapshot
  const items = selectedVersion.itemsSnapshot ?? []
  const hasCompare = !!compareVersion
  const modalTitle = hasCompare
    ? `Version ${compareVersion.versionNumber} → Version ${selectedVersion.versionNumber}`
    : `Version ${selectedVersion.versionNumber}`
  const modalDesc = hasCompare
    ? `Comparing snapshot from ${formatVersionDate(compareVersion.createdAt)} to ${formatVersionDate(selectedVersion.createdAt)}`
    : `Snapshot from ${formatVersionDate(selectedVersion.createdAt)} · by ${selectedVersion.createdBy}`

  return (
    <Modal
      isOpen description={modalDesc} footer={
        <div className="flex justify-between w-full">
          <Button size="sm" variant="secondary" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>
          <Button size="sm" variant="secondary" onClick={onClose}>Close</Button>
        </div>
      } size="full" title={modalTitle}
      onClose={onClose}
    >
      <div className="flex flex-col el-gap-md">
        {selectedVersion.changeNotes && (
          <div className="flex items-start gap-2.5 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg el-p-sm">
            <FileText className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">Reason for edit</p>
              <p className="text-sm text-blue-600 dark:text-blue-400">{selectedVersion.changeNotes}</p>
            </div>
          </div>
        )}
        <CompareBar compareVersion={compareVersion} isCompareLoading={isCompareLoading} selectedVersion={selectedVersion} versions={versions} onChangeCompare={onChangeCompare} />
        {hasCompare
          ? <SideBySidePanels baseCurrency={baseCurrency} compareVersion={compareVersion} selectedVersion={selectedVersion} />
          : <SnapshotPanel currency={baseCurrency} items={items} snap={snap} />
        }
      </div>
    </Modal>
  )
}
